import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useAppVersionCheck } from "@/hooks/useAppVersionCheck";

import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import EsqueciSenha from "@/pages/EsqueciSenha";
import RedefinirSenha from "@/pages/RedefinirSenha";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/Calendar";
import ReviewQueue from "@/pages/ReviewQueue";
import HearingsList from "@/pages/HearingsList";
import HearingDetail from "@/pages/HearingDetail";
import Clients from "@/pages/Clients";
import Compartilhamentos from "@/pages/Compartilhamentos";
import ExternoConvite from "@/pages/ExternoConvite";
import SettingsPage from "@/pages/Settings";
import Processos from "@/pages/Processos";
import ProcessoDetail from "@/pages/ProcessoDetail";
import PrazoCalculatorPage from "@/pages/PrazoCalculatorPage";
import Publicacoes from "@/pages/Publicacoes";
import ConfirmationPage from "@/pages/ConfirmationPage";
import TeamNoticePage from "@/pages/TeamNoticePage";
import EmailConfirmado from "@/pages/EmailConfirmado";
import Admin from "@/pages/Admin";
import Mensagens from "@/pages/Mensagens";
import Termo from "@/pages/Termo";
import CadastroAceite from "@/pages/CadastroAceite";
import Escritorio from "@/pages/Escritorio";
import Controladoria from "@/pages/Controladoria";
import ConviteEscritorio from "@/pages/ConviteEscritorio";

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/login" element={<Login />} />
      <Route path="/cadastro" element={<Signup />} />
      <Route path="/esqueci-senha" element={<EsqueciSenha />} />
      <Route path="/redefinir-senha" element={<RedefinirSenha />} />
      <Route path="/confirmar/:token" element={<ConfirmationPage />} />
      <Route path="/aviso-equipe/:token" element={<TeamNoticePage />} />
      <Route path="/externo/:shareId" element={<ExternoConvite />} />
      <Route path="/termo" element={<Termo />} />
      <Route path="/convite/:token" element={<ConviteEscritorio />} />
      <Route
        path="/aceite"
        element={
          <ProtectedRoute>
            <CadastroAceite />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-confirmado"
        element={
          <ProtectedRoute>
            <EmailConfirmado />
          </ProtectedRoute>
        }
      />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/calendario" element={<CalendarPage />} />
        <Route path="/calculadora-prazos" element={<PrazoCalculatorPage />} />
        <Route path="/revisao" element={<ReviewQueue />} />
        <Route path="/audiencias" element={<HearingsList />} />
        <Route path="/audiencia/:id" element={<HearingDetail />} />
        <Route path="/clientes" element={<Clients />} />
        <Route path="/compartilhamentos" element={<Compartilhamentos />} />
        <Route path="/equipe" element={<Navigate to="/compartilhamentos?aba=contatos" replace />} />
        <Route path="/mensagens" element={<Mensagens />} />
        <Route path="/processos" element={<Processos />} />
        <Route path="/processos/:id" element={<ProcessoDetail />} />
        {/* Lote 41: "Prazos" foi fundida em "Para validar" (revisão de ata) + a aba
            "Prazos" dentro de Audiências (HearingsList) — a rota antiga vira redirect
            pra não quebrar links/atalhos salvos. */}
        <Route path="/prazos" element={<Navigate to="/audiencias?filter=prazos" replace />} />
        <Route path="/agenda" element={<HearingsList />} />
        <Route path="/publicacoes" element={<Publicacoes />} />
        <Route path="/config" element={<SettingsPage />} />
        <Route path="/escritorio" element={<Escritorio />} />
        <Route path="/controladoria" element={<Controladoria />} />
        <Route path="/admin" element={<Admin />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Lote 77 — avisos (toasts) legíveis no iPhone/iPad. No topo da tela, no celular, o aviso caía
// sobre a barra de status/notch (parte "fosca") e ficava ilegível. No celular passa a aparecer
// embaixo, acima da barra de abas e da área segura do iPhone, com fundo opaco, texto maior
// e tempo de leitura mais longo. No computador continua no canto superior direito.
function ResponsiveToaster() {
  const [mobile, setMobile] = useState(() => {
    try { return window.matchMedia("(max-width: 767px)").matches; } catch { return false; }
  });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia("(max-width: 767px)"); } catch { return undefined; }
    const on = (e) => setMobile(e.matches);
    setMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", on); else mq.removeListener(on); };
  }, []);
  return (
    <Toaster
      position={mobile ? "bottom-center" : "top-right"}
      richColors
      theme="light"
      duration={mobile ? 6000 : 4000}
      mobileOffset={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 92px)", left: 12, right: 12 }}
      offset={{ top: 16, right: 16 }}
      toastOptions={{ className: "iapauta-toast" }}
    />
  );
}

export default function App() {
  // Lote 63: recarrega sozinho quando sai uma versão nova do app (ver o hook).
  useAppVersionCheck();
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            {/* theme="light" fixo: o app não tem modo escuro implementado (cores fixas no
                JSX, ver docs do projeto) — deixar o Toaster seguir o tema do sistema fazia
                o aviso aparecer ilegível (fundo escuro/texto escuro) no celular à noite ou
                com o telefone em modo escuro. Bug relatado pelo Robson (Lote 25). */}
            <ResponsiveToaster />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}
